import { Service } from 'typedi';
import { BaseAddEditViewModel } from '../../../../core/services/BaseViewModel';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Appl } from '../../../../core/Appl';
import { DeathReportModel } from './DeathReportModel';
import { IDeathReportService } from '../DeathReportService';
import { ListItem } from '../../../../core/services/BaseService';
import { ILovService } from '../../../utility/lov/LovService';
import { toServerDate } from '../../../../core/services/Helper';

@Service() // eslint-disable-next-line
export default class DeathReportViewModel extends BaseAddEditViewModel {
    private service: IDeathReportService;
    private lovService: ILovService;
    @observable public model: DeathReportModel = {
        mbrRelationId: undefined,
        reqPhoneType: '',
        mbrContactPrefId: undefined,
        reqAddressLine1: '',
        reqCity: '',
        reqState: '',
        reqZipCode: ''
    };
    @observable public phoneTypeList: Array<ListItem> = [];
    @observable public contactPrefList: Array<ListItem> = [];
    @observable public memberRelationList: Array<ListItem> = [];
    @observable public stepStatus: 'New' | 'Summary' | 'Confirmation' = 'New';
    @observable public submissionConfirmId: number = 0;

    @observable public id: number = 0;
    constructor() {
        super('Report a Death');
        this.service = Appl.Services.get<IDeathReportService>('IDeathReportService');
        this.lovService = Appl.Services.get<ILovService>('ILovService');
        makeObservable(this);
    }

    @action
    public async initAsync(): Promise<void> {
        this.mandaoryFieldsFilled = false;
        const phoneResult = await this.lovService.getListOfValuesAsync("phone_type");
        if (phoneResult.success) {
            runInAction(() => {
                this.phoneTypeList = phoneResult.data;
                // this.phoneTypeList.unshift({ key: '', value: '' });
            })
        }
        const contactPrefResult = await this.lovService?.getListOfValuesAsync("scers_member.lov_contact_pref");
        if (contactPrefResult.success) {
            runInAction(() => {
                this.contactPrefList = contactPrefResult.data;
                // this.contactPrefList.unshift({ key: '', value: '' });
            })
        }

        const relationShipResult = await this.lovService?.getListOfValuesAsync("scers_member.lov_mbr_relation");
        if (relationShipResult.success) {
            runInAction(() => {
                this.memberRelationList = relationShipResult.data;
                // this.memberRelationList.unshift({ key: '', value: '' });
            })
        }
    }

    @action
    public async onEditInfoAsync(): Promise<void> {
        this.stepStatus = 'New';
        this.pageTitle = 'Report a Death';
        this.model.captchaToken = undefined;
    }

    @action
    public getFormattedAddress(): string {
        let address = '';
        if (this.model.reqAddressLine1 && this.model.reqAddressLine1 !== "") {
            address += this.model.reqAddressLine1
        }
        if (this.model.reqCity && this.model.reqCity !== "") {
            if (this.model.reqAddressLine1 !== "") {
                address += `, `
            }
            address += this.model.reqCity;
        }
        if (this.model.reqState && this.model.reqState !== "") {
            if (this.model.reqAddressLine1 !== ""
                || this.model.reqCity !== "") {
                address += `, `
            }
            address += this.model.reqState;
        }
        if (this.model.reqZipCode && this.model.reqZipCode !== "") {
            address += ` ${this.model.reqZipCode}`
        }
        return address;
    }

    @action
    public async saveAsync(): Promise<void> {
        Appl.Validator.init();
        if (!this.model.captchaToken) {
            await Appl.Validator.setError("captcha", "Your valid captcha identity is required.");
        }
        await Appl.Validator.validateString('First Name', 'mbrFirstName', this.model.mbrFirstName, true, 100);
        await Appl.Validator.validateString('Last Name', 'mbrLastName', this.model.mbrLastName, true, 100);
        await Appl.Validator.validateNumber('SSN', 'mbrSsn', this.model.mbrSsn, true, undefined, undefined, 4, 4);
        await Appl.Validator.validateDate('Date of Birth', 'mbrBirthDate', this.model.mbrBirthDate, true, "mm/dd/yyyy");
        if (new Date(this.model.mbrBirthDate!) >= new Date()) {
            await Appl.Validator.setError("mbrBirthDate", "Birth Date should not be future date.");
        }
        await Appl.Validator.validateDate('Date of Death', 'mbrDeathDate', this.model.mbrDeathDate, true, "mm/dd/yyyy");
        if (new Date(this.model.mbrDeathDate!) > new Date()) {
            await Appl.Validator.setError("mbrDeathDate", "Death Date should not be future date.");
        }
        await Appl.Validator.validateString('First Name', 'reqFirstName', this.model.reqFirstName, true, 100);
        await Appl.Validator.validateString('Last Name', 'reqLastName', this.model.reqLastName, true, 100);
        await Appl.Validator.validateString('First Name', 'reqFirstName', this.model.reqFirstName, true, 100);
        await Appl.Validator.validateNumber('Relationship', 'mbrRelationId', this.model.mbrRelationId, true);
        await Appl.Validator.validateEmail('Email Address', 'reqEmail', this.model.reqEmail, true, 100);
        await Appl.Validator.validatePhone('Phone Number', 'reqPhone', this.model.reqPhone, true, 20);
        await Appl.Validator.validateZip('Zip Code', 'reqZipCode', this.model.reqZipCode, false, 5, 5);
        if (Appl.Validator.isValid()) {
            if (this.stepStatus === 'New') {
                this.stepStatus = 'Summary';
                this.pageTitle = 'Report a Death Summary';
                return;
            }
            this.model.mbrBirthDate = toServerDate(this.model.mbrBirthDate!);
            this.model.mbrDeathDate = toServerDate(this.model.mbrDeathDate!);
            const result = await this.service?.createDeathReportAsync(this.model);
            this.model.captchaToken = undefined;
            if (result.success) {
                this.model = {};
                this.submissionConfirmId = result.data;
                this.stepStatus = "Confirmation";
                this.pageTitle = 'Report a Death Submitted';
            } else {
                Appl.Error.handle(result.error);
                await this.onEditInfoAsync()
            }
        }
    }
}
